<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">User Management</h1>
        <p class="text-muted">Manage user accounts and permissions</p>
      </div>
      <div class="col-md-6 text-md-end">
        <div class="btn-group">
          <button type="button" class="btn btn-primary" (click)="createUser()">
            <i class="bi bi-plus-circle me-2"></i>
            Create User
          </button>
          <button type="button" class="btn btn-primary dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
            <span class="visually-hidden">Toggle Dropdown</span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" (click)="createUser()"><i class="bi bi-person-plus me-2"></i>Create User Only</a></li>
            <li><a class="dropdown-item" (click)="createUserOrganization()"><i class="bi bi-building-add me-2"></i>Create User + Organization</a></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by name or email..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Email Verified Filter -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Email Status</label>
            <select class="form-select" [(ngModel)]="filterEmailVerified" (change)="onFilterChange()">
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>

          <!-- 2FA Filter -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">2FA Status</label>
            <select class="form-select" [(ngModel)]="filter2FA" (change)="onFilterChange()">
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>

          <!-- Account Status Filter -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Account Status</label>
            <select class="form-select" [(ngModel)]="filterLocked" (change)="onFilterChange()">
              <option value="all">All</option>
              <option value="unlocked">Active</option>
              <option value="locked">Locked</option>
            </select>
          </div>

           <div class="col-md-auto">
              <label class="form-label small text-body-secondary">Membership Plan</label>
              <select class="form-select" [(ngModel)]="filterMembershipPlan" (change)="onFilterChange()">
                <option value="all">All</option>
                <option value="core">Core</option>
                <option value="pro">Pro</option>
              </select>
           </div>

          <!-- Sort -->
          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Sort By</label>
            <select class="form-select" [(ngModel)]="sortBy" (change)="onSortChange()">
              <option value="createdAt">Created Date</option>
              <option value="email">Email</option>
              <option value="firstName">Name</option>
            </select>
          </div>

          <div class="col-md-auto">
            <label class="form-label small text-body-secondary">Order</label>
            <select class="form-select" [(ngModel)]="sortOrder" (change)="onSortChange()">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          @if (showClearFilters()) {
            <div class="row mt-3">
              <div class="col-12">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetFilters()">
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Users Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>User</th>
              <th>Email Status</th>
              <th class="d-none d-lg-table-cell">2FA</th>
              <th>Account Status</th>
              <th title="Highest tier across this user's organizations. Display only — not used for feature access.">
                Membership
                <i class="bi bi-info-circle text-muted small"></i>
              </th>
              <th>Created</th>
              <th>Last Login Location</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="users.length === 0">
              <td colspan="8" class="text-center text-muted py-4">
                No users found
              </td>
            </tr>
            <tr *ngFor="let user of users" class="cursor-pointer" (click)="viewUser(user.id)">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar">
                    {{ (user.firstName || user.email || '?').charAt(0).toUpperCase() }}{{ (user.lastName || '').charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <div class="fw-medium">{{ user.firstName || '' }} {{ user.lastName || '' }}</div>
                    <small class="text-muted">{{ user.email }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span
                  class="badge"
                  [class.bg-success]="user.emailVerified"
                  [class.bg-warning]="!user.emailVerified"
                >
                  <i class="bi" [class.bi-check-circle]="user.emailVerified" [class.bi-exclamation-circle]="!user.emailVerified"></i>
                  {{ user.emailVerified ? 'Verified' : 'Unverified' }}
                </span>
              </td>
              <td class="d-none d-lg-table-cell">
                <span
                  class="badge"
                  [class.bg-success]="user.is2faEnabled"
                  [class.bg-secondary]="!user.is2faEnabled"
                >
                  <i class="bi" [class.bi-shield-check]="user.is2faEnabled" [class.bi-shield-x]="!user.is2faEnabled"></i>
                  {{ user.is2faEnabled ? 'Enabled' : 'Disabled' }}
                </span>
                <small *ngIf="user.is2faEnabled && user.twoFactorMethod" class="text-muted ms-1">
                  ({{ user.twoFactorMethod === '2fa-totp' ? 'TOTP' : 'SMS' }})
                </small>
              </td>
              <td>
                <span
                  class="badge"
                  [class.bg-success]="!user.isLocked"
                  [class.bg-danger]="user.isLocked"
                >
                  <i class="bi" [class.bi-unlock]="!user.isLocked" [class.bi-lock]="user.isLocked"></i>
                  {{ user.isLocked ? 'Locked' : 'Active' }}
                </span>
              </td>
              <td>
                @let membershipBadge = getMembershipPlanBadge(user.membershipPlan);
                <span [class]="membershipBadge.badge">
                  <i [class]="membershipBadge.icon"></i>
                  {{ membershipBadge.label }}
                </span>
              </td>
              <td>
                @let firstLocation = getLoginPlatformBadge(user.firstLoginLocation);
                @let created = formatDate(user.createdAt);
                <div class="vstack gap-1 align-items-start">
                  <!-- Mirrors the visible date below so the badge lands at the same
                       height as the single-badge cells. -->
                  <small class="text-muted invisible" aria-hidden="true">{{ created }}</small>
                  <span [class]="firstLocation.badge">
                    <i [class]="firstLocation.icon"></i>
                    @if (user.firstLoginLocation) {
                      {{ user.firstLoginLocation | titlecase }}
                    } @else {
                      Unknown
                    }
                  </span>
                  <small class="text-muted">{{ created }}</small>
                </div>
              </td>
              <td>
                @let lastLocation = getLoginPlatformBadge(user.lastLoginLocation);
                <span [class]="lastLocation.badge">
                  <i [class]="lastLocation.icon"></i>
                  @if (user.lastLoginLocation) {
                    {{ user.lastLoginLocation | titlecase }}
                  } @else {
                    Unknown
                  }
                </span>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  (click)="viewUser(user.id); $event.stopPropagation()"
                >
                  <i class="bi bi-pencil"></i>
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalUsers) }} of {{ totalUsers }} users
          </div>
          <nav *ngIf="totalPages > 1">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>
